import { normalizeGalleryLayout } from './galleryLayout';

export const CARD_SCHEMA_VERSION = 2;

/** Canonical section ids (no timestamp-based ids). */
export const DEFAULT_CARD_SECTIONS = [
  { id: 'basic-info', name: 'Basic Information', enabled: true, order: 1, fields: ['name', 'title', 'company', 'email', 'phone'] },
  { id: 'about', name: 'About Section', enabled: true, order: 2, fields: ['about'] },
  { id: 'social', name: 'Social Links', enabled: true, order: 3, fields: [] },
  { id: 'contact', name: 'Contact Information', enabled: true, order: 4, fields: [] },
  { id: 'appointment', name: 'Appointment/Calendar', enabled: false, order: 5, fields: ['calendarUrl', 'appointmentText'] },
  { id: 'custom-sections', name: 'Custom Sections', enabled: true, order: 6, fields: [] },
  { id: 'custom-links', name: 'Custom Links', enabled: true, order: 7, fields: [] },
  { id: 'gallery', name: 'Image Gallery', enabled: true, order: 8, fields: [] },
];

const LEGACY_SECTION_ID_MAP = {
  'about-section': 'about',
  'social-links': 'social',
  'contact-info': 'contact',
};

function canonicalSectionId(id) {
  return LEGACY_SECTION_ID_MAP[id] || id;
}

function normalizeSectionRow(s, order) {
  return {
    ...s,
    id: canonicalSectionId(s.id),
    order: order != null ? order : s.order,
  };
}

export function isStructuredCardV2(raw) {
  return raw && typeof raw === 'object' && raw.basicInfo != null && typeof raw.basicInfo === 'object';
}

function nextNumericIds(links) {
  const max = (links || []).reduce((m, l) => Math.max(m, Number(l.id) || 0), 0);
  return max + 1;
}

function parseStructuredV2(raw) {
  const bi = raw.basicInfo || {};
  const aboutSec = raw.aboutSection || {};
  const contact = raw.contactSection || {};
  const appt = raw.appointmentSection || {};
  const socialSec = raw.socialSection || {};
  const gallery = raw.gallerySection || {};

  const cardData = {
    customUrl: raw.customUrl || '',
    name: bi.name || '',
    title: bi.title || '',
    company: bi.company || '',
    email: bi.email || '',
    phone: bi.phone || '',
    website: bi.website || '',
    profileImageUrl: bi.profileImageUrl || '',
    bannerImageUrl: bi.bannerImageUrl || '',
    companyLogoUrl: bi.companyLogoUrl || '',
    about: aboutSec.about || '',
    createdAt: raw.createdAt || '',
    updatedAt: raw.updatedAt || '',
    address: contact.address || '',
    city: contact.city || '',
    state: contact.state || '',
    country: contact.country || '',
    googleMapsUrl: contact.googleMapsUrl || '',
    whatsapp: contact.whatsapp || '',
    additionalPhone: contact.additionalPhone || '',
    fax: contact.fax || '',
    gpay: contact.gpay || '',
    phonepe: contact.phonepe || '',
    paytm: contact.paytm || '',
    upiId: contact.upiId || '',
    officeHours: appt.officeHours || contact.officeHours || '',
    calendarUrl: appt.calendarUrl || '',
    appointmentText: appt.appointmentText || 'Book Appointment',
    profileImage: null,
    bannerImage: null,
  };

  const rawSocial = Array.isArray(socialSec.links) ? socialSec.links : raw.socialLinks || [];
  const socialLinks = rawSocial.map((l, i) => ({
    id: l.id != null ? l.id : i + 1,
    platform: l.platform || 'Custom',
    url: l.url || '',
    enabled: l.enabled !== false,
  }));

  const rawCustom = Array.isArray(raw.customLinks) ? raw.customLinks : [];
  const customLinks = rawCustom.map((l, i) => ({
    id: l.id != null ? l.id : i + 1,
    title: l.title || '',
    url: l.url || '',
    enabled: l.enabled !== false,
  }));

  const customSections = Array.isArray(raw.customSections)
    ? raw.customSections.map((c) => ({
        enabled: c.enabled !== false,
        title: c.title || '',
        description: c.description || '',
      }))
    : [];

  const gallerySection = {
    enabled: gallery.enabled !== false,
    layout: normalizeGalleryLayout(gallery.layout || gallery.galleryLayout),
    images: Array.isArray(gallery.images) ? gallery.images.map((img) => ({ ...img })) : [],
  };

  let sections = Array.isArray(raw.sections) && raw.sections.length
    ? sortSectionsByOrder(raw.sections).map((s, i) => normalizeSectionRow({ ...s }, i + 1))
    : DEFAULT_CARD_SECTIONS.map((s) => ({ ...s }));

  sections = ensureCanonicalSections(sections, {
    about: aboutSec.enabled !== false,
    social: socialSec.enabled !== false,
    contact: contact && contact.enabled !== false,
    appointment: appt.enabled === true,
    gallery: gallerySection.enabled !== false && gallerySection.images.length > 0,
  });

  return { cardData, sections, socialLinks, customLinks, customSections, gallerySection };
}

function sortSectionsByOrder(sections) {
  return [...(sections || [])].sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
}

function ensureCanonicalSections(sections, hints) {
  const byId = new Map(sortSectionsByOrder(sections).map((s) => [canonicalSectionId(s.id), { ...s, id: canonicalSectionId(s.id) }]));
  const out = DEFAULT_CARD_SECTIONS.map((def) => {
    const existing = byId.get(def.id);
    if (existing) {
      return {
        ...def,
        ...existing,
        name: def.name,
        id: def.id,
        order: existing.order ?? def.order,
      };
    }
    let enabled = def.enabled;
    if (def.id === 'about' && hints.about != null) enabled = hints.about;
    if (def.id === 'social' && hints.social != null) enabled = hints.social;
    if (def.id === 'contact' && hints.contact != null) enabled = hints.contact;
    if (def.id === 'appointment' && hints.appointment != null) enabled = hints.appointment;
    if (def.id === 'gallery' && hints.gallery != null) enabled = hints.gallery;
    return { ...def, enabled };
  });
  return sortSectionsByOrder(out).map((s, i) => ({ ...s, order: i + 1 }));
}

const CUSTOM_HEADER_RE = /^custom-\d+_header$/;

function parseLegacyFlat(raw) {
  const { sections: apiSections, profileImage: _p, bannerImage: _b, socialLinks: rawSocial, customLinks: rawCustom, templateId: _t, ...rest } = raw;

  const dynamicKeys = new Set();
  const customSections = [];
  const extraCustomLinks = [];
  let gallerySection = { enabled: true, layout: 'grid6', images: [] };

  for (const key of Object.keys(rest)) {
    if (CUSTOM_HEADER_RE.test(key)) {
      const base = key.replace(/_header$/, '');
      dynamicKeys.add(key);
      dynamicKeys.add(`${base}_description`);
      customSections.push({
        enabled: true,
        title: rest[key] || '',
        description: rest[`${base}_description`] || '',
      });
    }
  }

  let linkIdCounter = nextNumericIds(rawCustom || []);
  for (const key of Object.keys(rest)) {
    if (/^links-\d+_links$/.test(key) && Array.isArray(rest[key])) {
      dynamicKeys.add(key);
      rest[key].forEach((link) => {
        if (link && (link.title || link.url)) {
          extraCustomLinks.push({
            id: linkIdCounter++,
            title: link.title || '',
            url: link.url || '',
            enabled: true,
          });
        }
      });
    }
    if (/^images-\d+_images$/.test(key) && Array.isArray(rest[key])) {
      dynamicKeys.add(key);
      const layoutKey = key.replace(/_images$/, '_galleryLayout');
      if (rest[layoutKey] !== undefined) dynamicKeys.add(layoutKey);
      if (rest[key].length) {
        const mergedImages = [...gallerySection.images, ...rest[key].map((img) => ({ ...img }))];
        gallerySection = {
          enabled: true,
          layout: normalizeGalleryLayout(rest[layoutKey] || gallerySection.layout),
          images: mergedImages,
        };
      }
    }
  }

  const cardData = { ...rest };
  dynamicKeys.forEach((k) => delete cardData[k]);
  delete cardData.templateId;
  delete cardData.sections;
  delete cardData.socialLinks;
  delete cardData.customLinks;
  delete cardData._schemaVersion;

  const socialLinks = Array.isArray(rawSocial)
    ? rawSocial.map((l, i) => ({
        id: l.id != null ? l.id : i + 1,
        platform: l.platform || 'Custom',
        url: l.url || '',
        enabled: l.enabled !== false,
      }))
    : [];

  let customLinks = Array.isArray(rawCustom)
    ? rawCustom.map((l, i) => ({
        id: l.id != null ? l.id : i + 1,
        title: l.title || '',
        url: l.url || '',
        enabled: l.enabled !== false,
      }))
    : [];

  let nextId = nextNumericIds(customLinks);
  extraCustomLinks.forEach((l) => {
    customLinks.push({ ...l, id: nextId++ });
  });

  let sections = Array.isArray(apiSections) ? sortSectionsByOrder(apiSections).map((s) => normalizeSectionRow({ ...s })) : [];

  const filtered = sections.filter((s) => !/^custom-\d+$/.test(s.id) && !/^links-\d+$/.test(s.id) && !/^images-\d+$/.test(s.id));
  sections = filtered.length ? filtered : DEFAULT_CARD_SECTIONS.map((s) => ({ ...s }));

  sections = sections.map((s) => ({ ...s, id: canonicalSectionId(s.id) }));
  sections = dedupeSectionsById(sections);
  sections = ensureCanonicalSections(sections, {
    about: sections.find((s) => s.id === 'about')?.enabled !== false,
    social: sections.find((s) => s.id === 'social')?.enabled !== false,
    contact: sections.find((s) => s.id === 'contact')?.enabled !== false,
    appointment: sections.find((s) => s.id === 'appointment')?.enabled === true,
    gallery: gallerySection.images.length > 0,
  });

  if (customSections.length) {
    const cs = sections.find((s) => s.id === 'custom-sections');
    if (cs) cs.enabled = true;
  }

  return { cardData, sections, socialLinks, customLinks, customSections, gallerySection };
}

function dedupeSectionsById(sections) {
  const seen = new Set();
  const out = [];
  for (const s of sortSectionsByOrder(sections)) {
    const id = canonicalSectionId(s.id);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ ...s, id });
  }
  return out;
}

export function parseCardFromApi(raw) {
  if (!raw || typeof raw !== 'object') {
    return emptyEditorModel();
  }
  if (isStructuredCardV2(raw)) {
    return parseStructuredV2(raw);
  }
  return parseLegacyFlat(raw);
}

function emptyEditorModel() {
  return {
    cardData: {
      customUrl: '',
      name: '',
      title: '',
      company: '',
      email: '',
      phone: '',
      website: '',
      about: '',
      profileImage: null,
      profileImageUrl: '',
      bannerImage: null,
      bannerImageUrl: '',
      companyLogoUrl: '',
    },
    sections: DEFAULT_CARD_SECTIONS.map((s) => ({ ...s })),
    socialLinks: [
      { id: 1, platform: 'LinkedIn', url: '', enabled: true },
      { id: 2, platform: 'Twitter', url: '', enabled: true },
      { id: 3, platform: 'Instagram', url: '', enabled: true },
      { id: 4, platform: 'Facebook', url: '', enabled: true },
    ],
    customLinks: [
      { id: 1, title: 'Portfolio', url: '', enabled: true },
      { id: 2, title: 'Resume', url: '', enabled: true },
    ],
    customSections: [],
    gallerySection: { enabled: true, layout: 'grid6', images: [] },
  };
}

export function toDisplayCard(raw) {
  const m = parseCardFromApi(raw);
  return {
    ...m.cardData,
    sections: m.sections,
    socialLinks: m.socialLinks,
    customLinks: m.customLinks,
    customSections: m.customSections,
    gallerySection: m.gallerySection,
  };
}

function sectionEnabled(sections, id) {
  const s = sections.find((x) => x.id === id);
  return s ? !!s.enabled : true;
}

/**
 * Payload stored in `user_cards.card_data` (nested schema).
 */
export function serializeStructuredCard({
  cardData,
  sections,
  socialLinks,
  customLinks,
  customSections,
  gallerySection,
}) {
  const now = new Date().toISOString();
  return {
    _schemaVersion: CARD_SCHEMA_VERSION,
    customUrl: cardData.customUrl || '',
    basicInfo: {
      name: cardData.name || '',
      title: cardData.title || '',
      company: cardData.company || '',
      email: cardData.email || '',
      phone: cardData.phone || '',
      website: cardData.website || '',
      profileImageUrl: cardData.profileImageUrl || '',
      bannerImageUrl: cardData.bannerImageUrl || '',
      companyLogoUrl: cardData.companyLogoUrl || '',
    },
    aboutSection: {
      enabled: sectionEnabled(sections, 'about'),
      about: cardData.about || '',
    },
    socialSection: {
      enabled: sectionEnabled(sections, 'social'),
      links: (socialLinks || [])
        .filter((l) => l.enabled && String(l.url || '').trim())
        .map((l) => ({ platform: l.platform || 'Custom', url: l.url })),
    },
    contactSection: {
      enabled: sectionEnabled(sections, 'contact'),
      address: cardData.address || '',
      city: cardData.city || '',
      state: cardData.state || '',
      country: cardData.country || '',
      googleMapsUrl: cardData.googleMapsUrl || '',
      whatsapp: cardData.whatsapp || '',
      additionalPhone: cardData.additionalPhone || '',
      fax: cardData.fax || '',
      gpay: cardData.gpay || '',
      phonepe: cardData.phonepe || '',
      paytm: cardData.paytm || '',
      upiId: cardData.upiId || '',
    },
    appointmentSection: {
      enabled: sectionEnabled(sections, 'appointment'),
      calendarUrl: cardData.calendarUrl || '',
      appointmentText: cardData.appointmentText || 'Book Appointment',
      officeHours: cardData.officeHours || '',
    },
    customSections: (customSections || []).map((c) => ({
      enabled: c.enabled !== false,
      title: c.title || '',
      description: c.description || '',
    })),
    customLinks: (customLinks || [])
      .filter((l) => l.enabled)
      .map((l) => ({
        enabled: true,
        title: l.title || '',
        url: l.url || '',
      })),
    gallerySection: {
      enabled: sectionEnabled(sections, 'gallery'),
      layout: normalizeGalleryLayout(gallerySection?.layout),
      images: (gallerySection?.images || [])
        .filter((img) => img && img.url)
        .map((img) => ({
          title: img.title || '',
          filename: img.filename || '',
          url: img.url,
        })),
    },
    sections: sortSectionsByOrder(sections || []).map((s, i) => ({
      id: s.id,
      name: s.name,
      enabled: !!s.enabled,
      order: i + 1,
    })),
    updatedAt: cardData.updatedAt || now,
    createdAt: cardData.createdAt || now,
  };
}

export const LOCKED_SECTION_IDS = new Set([
  'basic-info',
  'about',
  'social',
  'contact',
  'appointment',
  'custom-sections',
  'custom-links',
  'gallery',
]);
